// src/pages/2_Domains.js
import React, { useState, useContext, useMemo, useRef, useEffect } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../../components/Layout';
import SSIDomainQuestionPanel from './components/SSIDomainQuestionPanel';
import Breadcrumb from '../../../components/Breadcrumb';
import SSISideNavbar from './components/SSISideNavbar';
import { auth } from '../../../firebase';
import {
  isClinicianAssignedToStage,
  isSSILeadOverrideActive,
  isLeadClinician,
} from '../../../utils/clinicianPermissions';
import SSIResetBanner from '../../../components/SSIResetBanner';
import SSIOverrideBanner from '../../../components/SSIOverrideBanner';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import useSsiLockGuard from '../../../hooks/useSsiLockGuard';

export default function SemiStructuredInterviewDomains() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { patientDataModel, fetchPatientData, updatePatientFirestore } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // One-clinician SLaM: bounce back to the overview while the SSI is locked
  // (awaiting the end-of-CI decision, or the assessment has been ended).
  useSsiLockGuard({ patientDataModel, wf, patientId });

  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    // Check if the current clinician is assigned to the SSI stage
    if (auth.currentUser && patientDataModel) {
      const hasPermission = isClinicianAssignedToStage(
        patientDataModel,
        auth.currentUser.uid,
        'SSI'
      );          
      setCanEdit(hasPermission);
    }
  }, [patientDataModel]);

  // Check if SSI reset is in progress (3 Borough / SLaM workflow)
  const ssiResetInfo = useMemo(() => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    if (!manageReports?.ssiResetRequired) {
      return { isResetRequired: false };
    }
    return {
      isResetRequired: true,
      resetRequestedByName: manageReports.ssiResetRequestedByName || 'Lead clinician',
      resetReason: manageReports.ssiResetReason || 'Conflict with Lead clinician findings',
      conflictType: manageReports.ssiResetConflictType || 'CI',
    };
  }, [patientDataModel]);

  // Lead takeover banner — mirrors Symptoms / Review / RD_SSI so the original
  // SSI clinician sees they are viewing the Lead's live proforma read-only.
  const ssiOverrideActive = isSSILeadOverrideActive(patientDataModel);
  const leadOverride =
    patientDataModel?.dataModel?.['3_semiStructuredInterview']?.leadOverride;
  const snapshotPath = leadOverride?.archiveId
    ? `/patient/${patientId}/ssi-snapshot/${leadOverride.archiveId}`
    : null;
  const isCurrentUserLead = Boolean(
    auth.currentUser?.uid &&
      isLeadClinician(patientDataModel, auth.currentUser.uid)
  );

  const currentStageTitle = 'Semi-Structured Interview';
  const currentSectionTitle = "Domains of Functional Impairment";
  const currentSectionNumber = 2 

  const SECTIONS_SSI = useMemo(() => Object.entries(patientDataModel['dataModel']['3_semiStructuredInterview']['sections']).map(([key, section]) => ({
    key,
    ...section
  })), [patientDataModel]);

  const questions = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === currentSectionTitle
    );
    if (!section) {
      console.error(`Section with title "${currentSectionTitle}" not found.`);
      return [];
    }
    return Object.entries(section.questions || {}).map(([key, question]) => question);
  }, [SECTIONS_SSI, currentSectionTitle]);

  const [selections, setSelections] = useState(
    questions.map((question) => ({
      adulthood: question.functioningReduced_Adulthood !== "" ? question.functioningReduced_Adulthood : null,
      childhood: question.functioningReduced_Childhood !== "" ? question.functioningReduced_Childhood : null,
    }))
  );  

  const [notes, setNotes] = useState(
    questions.map((question) => ({
      adulthood: question.clinicianNotes_Adulthood || '',
      childhood: question.clinicianNotes_Childhood || '',
    }))
  );

  const [activeRecording, setActiveRecording] = useState(null);
  const [recognition, setRecognition] = useState(null);

  // Refs for text areas
  const textAreaRefs = useRef([]);

  useEffect(() => {
    // Ensure textAreaRefs is initialized with the correct structure
    if (questions.length > 0) {
      textAreaRefs.current = questions.map((_, i) => ({
        adulthood: textAreaRefs.current[i]?.adulthood || React.createRef(),
        childhood: textAreaRefs.current[i]?.childhood || React.createRef(),
      }));
    }
  }, [questions]);
  
  // Handle selection of symptoms
  const handleSelection = (index, type, value) => {
    const newSelections = [...selections];
    newSelections[index][type] = newSelections[index][type] === value ? null : value;
    setSelections(newSelections);
  };

  // Handle note changes
  const handleNoteChange = (index, type, text) => {
    setNotes((prevNotes) => {
      const newNotes = [...prevNotes];
      newNotes[index][type.toLowerCase()] = text;
      return newNotes;
    });
  };

  // Handle speech recognition for notes
  const handleRecording = (index, type) => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }

    if (recognition) {
      recognition.stop();
      return;
    }

    const newRecognition = new window.webkitSpeechRecognition();
    newRecognition.continuous = true;
    newRecognition.interimResults = true;
    newRecognition.lang = 'en-GB';

    newRecognition.onresult = (event) => {
      let finalTranscript = notes[index][type];
      for (let i = event.resultIndex; i < event.results.length; i++) {
        let transcript = event.results[i][0].transcript.trim();
        if (event.results[i].isFinal) {
          transcript = ' ' + transcript.charAt(0).toUpperCase() + transcript.slice(1) + '.';
          finalTranscript += transcript;
        }
      }
      handleNoteChange(index, type, finalTranscript);
    };

    newRecognition.onend = () => {
      setActiveRecording(null);
      setRecognition(null);
  
      // Simulate focus and blur to trigger onBlur
      if (textAreaRefs.current[index] && textAreaRefs.current[index][type]?.current) {
        textAreaRefs.current[index][type].current.focus();
        setTimeout(() => {
          textAreaRefs.current[index][type].current.blur();
        }, 0); // Use a timeout to ensure the focus is set before blurring
      }
    };
  
    newRecognition.start();
    setRecognition(newRecognition);
    setActiveRecording({ index, type });
  
    // Focus the text area when recording starts
    if (textAreaRefs.current[index] && textAreaRefs.current[index][type]?.current) {
      textAreaRefs.current[index][type].current.focus();
    }
  };

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);

  // Handle navigation state from the unified sidebar (cross-section jump)
  useEffect(() => {
    if (location.state?.jumpToQuestion !== undefined) {
      const targetIndex = location.state.jumpToQuestion;
      if (targetIndex >= 0 && targetIndex < questions.length) {
        setActiveQuestionIndex(targetIndex);
      }
      window.history.replaceState({}, '');
    }
  }, [location.state, questions.length]);

  const jumpToQuestion = (index) => {
    setActiveQuestionIndex(index);
    // Scroll to top of the main content area when navigating between questions
    // The Layout component uses a flex container where the main content is in a scrollable div
    const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
    if (mainContentArea) {
      mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Fallback to window scroll if the container isn't found
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const breadcrumbTuples = [
    
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* SSI Reset / Lead takeover banners (3 Borough / SLaM workflow) */}
      <div className="px-8">
          <SSIResetBanner
            isVisible={ssiResetInfo.isResetRequired}
            resetRequestedByName={ssiResetInfo.resetRequestedByName}
            conflictType={ssiResetInfo.conflictType}
          />
          {ssiOverrideActive && (
            <div className="mt-3">
              <SSIOverrideBanner
                isVisible
                originalClinicianName={
                  leadOverride?.originalClinicianName ||
                  patientDataModel?.clinicians?.clinicianSSI?.clinicianName ||
                  'the original SSI clinician'
                }
                snapshotPath={snapshotPath}
                isCurrentUserLead={isCurrentUserLead}
              />
            </div>
          )}
      </div>
      <div className="flex">
        <SSISideNavbar
          questions={questions}
          activeQuestionIndex={activeQuestionIndex}
          selections={selections}
          jumpToQuestion={jumpToQuestion}
          currentSectionTitle={currentSectionTitle}
        />
        <div className="flex-grow">          
        <SSIDomainQuestionPanel
            currentStageTitle={currentStageTitle}
            currentSectionTitle={currentSectionTitle}
            currentSectionNumber={currentSectionNumber}
            questions={questions}
            question={questions[activeQuestionIndex]}
            index={activeQuestionIndex}
            selections={selections}
            notes={notes}
            activeRecording={activeRecording}
            handleSelection={handleSelection}
            handleNoteChange={handleNoteChange}
            handleRecording={handleRecording}
            jumpToQuestion={jumpToQuestion}
            patientDataModel={patientDataModel}
            fetchPatientData={fetchPatientData}
            updatePatientFirestore={updatePatientFirestore}
            patientId={patientId}
            navigate={navigate}
            textAreaRefs={textAreaRefs}
            canEdit={canEdit}
          />
        </div>
      </div>
    </Layout>
  );
}

